import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  IconButton,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Rule, Gateway, ValidationResult } from '../../types/dynamicRouting';
import RuleEditor from './RuleEditor';

interface SortableRuleCardProps {
  rule: Rule;
  index: number;
  gateways: Gateway[];
  onChange: (index: number, rule: Rule) => void;
  onRemove: (index: number) => void;
  validationResult?: ValidationResult;
  collapseSignal?: number;
  totalRules: number;
}

const SortableRuleCard: React.FC<SortableRuleCardProps> = ({
  rule,
  index,
  gateways,
  onChange,
  onRemove,
  validationResult,
  collapseSignal,
  totalRules,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: rule.id,
  } as any);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const validationMessages = validationResult
    ? validationResult.messages.filter(
      (msg) =>
        msg.path.includes(`rules[${index}]`) ||
        msg.path.includes(`rules/${index}`)
    )
    : [];

  const hasErrors = validationMessages.some((msg) => msg.level === 'error');

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 3,
        border: hasErrors ? '2px solid #f44336' : '1px solid #e0e0e0',
        borderRadius: 2,
      }}
    >
      <CardContent>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
        >
          {/* Left side: drag handle + rule index */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              {...attributes}
              {...listeners}
              sx={{ cursor: 'grab' }}
              size="small"
            >
              <DragIndicatorIcon />
            </IconButton>
            <Typography variant="h6" color="primary">
              # {index + 1}
            </Typography>
          </Box>

          {/* Rule name */}
          <Typography variant="body2" color="text.secondary">
            {rule.name || 'Unnamed Rule'}
          </Typography>
        </Box>

        {/* Validation messages */}
        {validationMessages.map((msg, msgIndex) => (
          <Alert key={msgIndex} severity={msg.level} sx={{ mb: 2 }}>
            <strong>[{msg.code}]</strong> {msg.message}
            {msg.suggestion && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                💡 {msg.suggestion}
              </Typography>
            )}
          </Alert>
        ))}

        {/* Rule editor */}
        <RuleEditor
          rule={rule}
          gateways={gateways}
          onChange={(updatedRule) => onChange(index, updatedRule)}
          onRemove={() => onRemove(index)}
          canRemove={totalRules > 1}
          validationResult={validationResult}
          rulePath={`rules[${index}]`}
          level={0}
          collapseSignal={collapseSignal}
        />
      </CardContent>
    </Card>
  );
};

export default SortableRuleCard;
